/**
 * Send Wave - das Fenster zum Zusammenstellen einer Welle
 * (Masterprompt Abschnitte 70-74, VISUAL_REFERENCE Abschnitte 29 bis 46).
 *
 * Aufbau seit v0.31:
 *
 *   Kopf     Ueberschrift, Untertitel, grosses X
 *   Reiter   ALL und Tier I bis IV; das naechste Tier laesst sich hier kaufen
 *   Karten   Creep-Karten mit Bild, Preis, Income und Rollen
 *   Reihen   FRONT, CENTER, BACK als VORSCHAU der Ankunftsreihenfolge
 *   Rechts   WAVE SUMMARY, die drei Speicherplaetze, LAST WAVE, CLEAR und
 *            ganz unten der grosse goldene SEND WAVE
 *
 * Die drei Reihen sind seit v0.30 keine Ablage mehr: Wohin ein Creep kommt,
 * steht an ihm selbst (siehe wave-rows.ts). Man sieht hier also, in welcher
 * Reihenfolge die Welle beim Gegner ankommt - und nimmt mit Minus und Plus
 * Stueck fuer Stueck nach.
 *
 * Eine Welle darf auch geplant werden, wenn das Gold noch nicht reicht.
 * Alles hier ist Vorschau - ob gesendet wird, entscheidet der Server.
 */

import { MAX_SENT_CREEPS_PER_LANE, getCreep, type CreepDefinition } from '@dueline/game-data';
import { flattenWave, isBattlePhase } from '@dueline/game-rules';
import type { MatchPhase } from '@dueline/shared';
import { useMemo, useState } from 'react';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import {
  WAVE_ROWS,
  creepInitials,
  getCreepsForTab,
  canAddCreep,
  getSendBlocker,
  getTierTabState,
  groupRow,
  isDraftEmpty,
  summarizeDraft,
  type TierTab,
  type WaveDraft,
} from './composerModel.js';
import { formatGold, formatTier } from './economyView.js';
import { erstelleLokalenSpeicher, hoechstesTier, setzeSlot, type WaveSlots } from './waveSlots.js';
import { useHoldRepeat, useLongPress, useTap } from './usePress.js';
import { creepKarte, creepKopf } from './faehigkeitBilder.js';

/*
 * Nur die vier Tiers - kein ALL mehr (Produktentscheidung vom 27.09.2026).
 *
 * Man soll bewusst in ein Tier gehen, um daraus zu schicken. Das macht die
 * Auswahl kleiner und die Entscheidung klarer - und alle Karten eines Tiers
 * passen nebeneinander, ohne zu wischen.
 */
const TABS: readonly TierTab[] = [1, 2, 3, 4];

/**
 * Die runde Marke eines Creeps: das nahe Kopfbild (18.10.2026), ohne Bild
 * wie bisher das Kuerzel auf der Farbe der Rolle.
 */
export function CreepToken({ creep, size = 30 }: { creep: CreepDefinition; size?: number }) {
  const rolle = creep.tags[0] ?? 'melee';
  const kopf = creepKopf(creep.id);
  return (
    <span
      className={`dl-creep-token dl-creep-token--${rolle}` + (kopf ? ' dl-creep-token--bild' : '')}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-hidden="true"
    >
      {kopf ? (
        <picture className="dl-creep-token__bild">
          <source type="image/avif" srcSet={kopf.avif} sizes="64px" />
          <source type="image/webp" srcSet={kopf.webp} sizes="64px" />
          <img src={kopf.ersatz} alt="" decoding="async" />
        </picture>
      ) : (
        creepInitials(creep.name)
      )}
    </span>
  );
}

/**
 * Die Rolle, die einen Creep am ehesten beschreibt.
 *
 * Die Reihenfolge ist die Rangfolge: Ein Boss bleibt ein Boss, ein Panzer ein
 * Panzer. "Nahkampf" und "Fernkampf" stehen ganz hinten - sie sagen am
 * wenigsten, und man sieht sie schon an der Reihe, in der der Creep landet.
 */
const ROLLEN_RANG = [
  'boss',
  'tank',
  'siege',
  'summoner',
  'support',
  'economy',
  'fast',
  'ranged',
  'melee',
] as const;

function hauptRolle(creep: CreepDefinition): string {
  const tags = creep.tags as readonly string[];
  return ROLLEN_RANG.find((rolle) => tags.includes(rolle)) ?? tags[0] ?? 'melee';
}

function tagLabel(tag: string): string {
  const labels = t.army.tags as Record<string, string>;
  return labels[tag] ?? tag;
}

/**
 * Eine Creep-Karte. Tippen fuegt einen hinzu, Halten fuegt mehrere hinzu.
 *
 * Aufbau nach Abschnitt 32: grosses Bild, Name, Goldpreis, Income, Rollen.
 * Die Farben tragen Bedeutung (Abschnitt 33): Gold fuer den Preis, Gruen fuer
 * Income, blaugraue Plaketten fuer die Rollen.
 */
function CreepCard({
  creep,
  blocker,
  requiresTier,
  onAdd,
}: {
  creep: CreepDefinition;
  /**
   * Warum dieser Creep gerade nicht in die Welle darf - oder null.
   *
   * Rueckmeldung aus dem Spieltest (24.09.2026): Man konnte alles in die Welle
   * legen und erfuhr erst beim SEND, dass das Gold nicht reicht.
   */
  blocker: 'tier_locked' | 'not_enough_gold' | 'too_many_points' | 'lane_full' | null;
  /** Ab welchem Tier er verfuegbar waere - nur bei gesperrten Karten. */
  requiresTier: number | null;
  onAdd: () => void;
}) {
  const locked = blocker !== null;
  const press = useHoldRepeat(onAdd);
  const boss = creep.tags.includes('boss');
  const faehigkeit =
    creep.specialBehaviour !== 'none'
      ? (t.army.abilities as Record<string, string>)[creep.specialBehaviour]
      : null;
  /*
   * Die Plakette der Sonderfaehigkeit ERSETZT die der Rolle, wenn es eine
   * gibt - sie sagt mehr. Nicht alle Faehigkeiten haben eine: "viel Income"
   * steht schon als ECONOMY da.
   */
  const kannBesonderes =
    (t.army.abilityTags as Record<string, string>)[creep.specialBehaviour] ?? null;
  const karte = creepKarte(creep.id);

  return (
    <button
      type="button"
      className={
        'dl-creep-card' +
        (boss ? ' dl-creep-card--boss' : '') +
        (locked ? ' dl-creep-card--locked' : '')
      }
      title={faehigkeit ? `${creep.name} - ${faehigkeit}` : creep.name}
      aria-label={`${creep.name}: ${creep.cost} Gold, +${creep.incomeGain} Income, ${creep.armyPoints} Army Points${faehigkeit ? `. ${faehigkeit}` : ''}`}
      disabled={locked}
      {...(locked ? {} : press)}
    >
      {/* Das Bild nimmt einen guten Teil der Karte ein (Abschnitt 32). */}
      {/*
        Mit Kartenbild (18.10.2026) fuellt das Bild die ganze Flaeche statt
        der Marke in der Mitte.
      */}
      <span className={'dl-creep-card__portrait' + (karte ? ' dl-creep-card__portrait--bild' : '')}>
        {karte ? (
          <picture className="dl-creep-card__bild">
            <source type="image/avif" srcSet={karte.avif} sizes="192px" />
            <source type="image/webp" srcSet={karte.webp} sizes="192px" />
            <img src={karte.ersatz} alt="" decoding="async" />
          </picture>
        ) : (
          <CreepToken creep={creep} size={56} />
        )}
        {requiresTier !== null && (
          <span className="dl-creep-card__lock" aria-hidden="true">
            ⌧
          </span>
        )}
      </span>

      <span className="dl-creep-card__name">{creep.name}</span>

      {/* Goldkosten und Income - beides als blanke Zahl, Gold und Gruen. */}
      <span className="dl-creep-card__prices">
        <span className="dl-creep-card__cost">{creep.cost}</span>
        <span className="dl-creep-card__income">+{creep.incomeGain}</span>
      </span>

      {/*
        Die Plaketten stehen ganz unten und duerfen zwei Zeilen brauchen
        (Produktentscheidung vom 27.09.2026). Damit die Karten trotzdem alle
        gleich hoch bleiben, ist in der Anzeige immer Platz fuer zwei Zeilen
        reserviert - auch wenn nur eine gebraucht wird.

        Links steht, was den Creep ausmacht: seine Sonderfaehigkeit, wenn er
        eine hat, sonst seine wichtigste Rolle. Daneben die Army Points.
      */}
      <span className="dl-creep-card__tags">
        <span
          className={
            'dl-creep-card__tag ' +
            (kannBesonderes
              ? 'dl-creep-card__tag--ability'
              : `dl-creep-card__tag--${hauptRolle(creep)}`)
          }
        >
          {kannBesonderes ?? tagLabel(hauptRolle(creep))}
        </span>
        <span className="dl-creep-card__tag dl-creep-card__tag--points">
          {creep.armyPoints} {t.army.card.points}
        </span>
      </span>

      {/*
        Bei einem gesperrten Tier steht hier NICHTS (Rueckmeldung vom
        27.09.2026): Dass Tier II erst freigeschaltet werden muss, sagen schon
        der Reiter oben und das Schloss auf dem Bild. Dreimal dasselbe ist
        zweimal zu viel.
      */}
      {requiresTier === null && blocker && (
        <span className="dl-creep-card__blocked">{t.army.blocked[blocker]}</span>
      )}
    </button>
  );
}

/**
 * Ein Eintrag in einer Reihe: Symbol, Anzahl, Minus und Plus
 * (VISUAL_REFERENCE Abschnitt 39).
 *
 * Das Plus ist absichtlich hier und nicht nur auf der Karte: Wer die Welle
 * nachjustiert, schaut auf die Reihen, nicht mehr auf die Kartenleiste.
 */
/**
 * Ein Speicherplatz (VISUAL_REFERENCE Abschnitt 44).
 *
 * Kurz tippen laedt die Welle, lange druecken speichert die aktuelle darauf -
 * dieselbe Bedienung wie bei LAST WAVE. Eine Bedienart weniger zu lernen
 * (Produktentscheidung vom 27.09.2026).
 */
/**
 * Was eine gespeicherte Welle gerade kostet - und ob man sie sich leisten kann.
 *
 * Auf einem Platz stehen nur die Goldkosten, nicht die Creeps (Rueckmeldung vom
 * 27.09.2026, seit dem 28.09.2026 auch bei LAST WAVE): Eine Symbolreihe waechst
 * mit der Welle und haette die Zeile irgendwann gesprengt. Eine Zahl ist immer
 * gleich breit.
 *
 * Zwei Zustaende sieht man sofort:
 *   ausgegraut  das Gold reicht nicht
 *   roetlich    das noetige Tier ist noch nicht freigeschaltet
 *
 * Das Tier wiegt schwerer: Gold sammelt sich von selbst, ein Tier nicht.
 */
function wellenZustand(welle: WaveDraft | null, snapshot: MatchSnapshot) {
  const kosten = welle ? summarizeDraft(welle, snapshot).cost : 0;
  const tierFehlt = welle ? hoechstesTier(welle) > snapshot.tier : false;
  const zuTeuer = welle ? kosten > snapshot.gold : false;

  return {
    kosten,
    tierFehlt,
    zuTeuer,
    klassen:
      (welle ? ' dl-slot-wave--filled' : '') +
      (tierFehlt ? ' dl-slot-wave--tier' : '') +
      (!tierFehlt && zuTeuer ? ' dl-slot-wave--poor' : ''),
    /** Der Teil der Vorlesehilfe, der den Grund nennt. */
    grund:
      (tierFehlt ? `. ${t.army.blocked.tier_locked}` : '') +
      (zuTeuer ? `. ${t.army.blocked.not_enough_gold}` : ''),
  };
}

function WaveSlotButton({
  nummer,
  welle,
  snapshot,
  onLoad,
  onSave,
}: {
  nummer: number;
  welle: WaveDraft | null;
  snapshot: MatchSnapshot;
  onLoad: () => void;
  onSave: () => void;
}) {
  const press = useLongPress(onLoad, onSave);
  const zustand = wellenZustand(welle, snapshot);

  return (
    <button
      type="button"
      className={'dl-slot-wave' + zustand.klassen}
      title={`${t.army.saveSlots} ${nummer} - ${t.army.slotHint}`}
      aria-label={
        welle
          ? `${t.army.saveSlots} ${nummer}: ${formatGold(zustand.kosten)} ${t.economy.gold}` +
            zustand.grund
          : `${t.army.saveSlots} ${nummer}: ${t.army.slotEmpty}`
      }
      {...press}
    >
      <span className="dl-slot-wave__number">{nummer}</span>
      {welle ? (
        <span className="dl-slot-wave__cost">{formatGold(zustand.kosten)}</span>
      ) : (
        <span className="dl-slot-wave__empty">{t.army.slotEmpty}</span>
      )}
    </button>
  );
}

function WaveEntry({
  creep,
  count,
  canAdd,
  onAdd,
  onRemove,
}: {
  creep: CreepDefinition;
  count: number;
  canAdd: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  const minus = useTap(onRemove);
  const plus = useTap(onAdd);

  return (
    <span className="dl-wave-entry">
      <CreepToken creep={creep} size={32} />
      <button
        type="button"
        className="dl-wave-entry__step"
        aria-label={`${t.army.removeHint}: ${creep.name}`}
        {...minus}
      >
        −
      </button>
      <span className="dl-wave-entry__count">{count}</span>
      <button
        type="button"
        className="dl-wave-entry__step"
        aria-label={`${creep.name} hinzufuegen`}
        disabled={!canAdd}
        {...plus}
      >
        +
      </button>
    </span>
  );
}

export function ArmyComposer({
  snapshot,
  draft,
  onAdd,
  onRemove,
  onClear,
  onLoad,
  onSend,
  onClose,
  onUpgradeTier,
}: {
  snapshot: MatchSnapshot;
  draft: WaveDraft;
  onAdd: (creepId: string) => void;
  onRemove: (creepId: string) => void;
  onClear: () => void;
  onLoad: (wave: WaveDraft) => void;
  onSend: () => void;
  onClose: () => void;
  onUpgradeTier: () => void;
}) {
  // Der Reiter startet beim hoechsten freigeschalteten Tier - dort sind die
  // staerksten Creeps, die man gerade senden kann.
  const [tab, setTab] = useState<TierTab>(snapshot.tier as TierTab);
  const tabState = getTierTabState(tab, snapshot.tier, snapshot.gold);

  /*
   * Die Speicherplaetze liegen im Browser und ueberleben das Match. Der
   * Speicher steckt hinter einer Schnittstelle - sobald es Konten gibt, wird
   * hier ein serverseitiger eingesetzt, und sonst aendert sich nichts
   * (Produktentscheidung vom 27.09.2026, siehe waveSlots.ts).
   */
  const speicher = useMemo(
    () => erstelleLokalenSpeicher(typeof window === 'undefined' ? null : window.localStorage),
    [],
  );
  const [slots, setSlots] = useState<WaveSlots>(() => speicher.lesen());

  const speichereAuf = (index: number) => {
    if (isDraftEmpty(draft)) return;
    const neu = setzeSlot(slots, index, draft);
    setSlots(neu);
    speicher.schreiben(neu);
  };

  const summary = summarizeDraft(draft, snapshot);
  const einheiten = flattenWave(draft).length;
  const blocker = getSendBlocker(
    summary,
    isBattlePhase(snapshot.phase as MatchPhase),
    snapshot.waveCooldown,
    snapshot.enemyLaneCreeps,
    einheiten,
  );
  const ueberCap = summary.armyPoints > summary.armyPointCap;
  const laneVoll = snapshot.enemyLaneCreeps + einheiten > MAX_SENT_CREEPS_PER_LANE;
  const letzteWelle = snapshot.waveHistory[0] ?? null;
  const letzterZustand = wellenZustand(letzteWelle, snapshot);

  const schliessen = useTap(onClose);
  const leeren = useTap(onClear);
  const senden = useTap(onSend);
  const letzteLaden = useTap(() => {
    if (letzteWelle) onLoad(letzteWelle);
  });

  return (
    <div className="dl-composer" role="dialog" aria-label={t.army.title}>
      <div className="dl-composer__head">
        <span className="dl-composer__title">{t.army.title}</span>
        <button
          type="button"
          className="dl-composer__close"
          aria-label={t.army.close}
          {...schliessen}
        >
          ✕
        </button>
      </div>

      <div className="dl-composer__main">
        {/* Reiter: ALL und die vier Tiers. Hoehere sind sichtbar, aber gesperrt. */}
        <div className="dl-composer__tiers" role="tablist">
          {TABS.map((eintrag) => (
            <button
              key={String(eintrag)}
              type="button"
              role="tab"
              aria-selected={eintrag === tab}
              className={[
                'dl-composer__tier-tab',
                eintrag === tab ? 'dl-composer__tier-tab--active' : '',
                eintrag > snapshot.tier ? 'dl-composer__tier-tab--locked' : '',
              ].join(' ')}
              onClick={() => setTab(eintrag)}
            >
              {`${t.army.tierTab} ${formatTier(eintrag)}`}
            </button>
          ))}

          {tabState.kind === 'next' && (
            <button
              type="button"
              className="dl-composer__unlock"
              onClick={onUpgradeTier}
              disabled={!tabState.affordable || !isBattlePhase(snapshot.phase as MatchPhase)}
            >
              {t.army.tierTab} {formatTier(tab)} {t.army.tierUnlock} · {formatGold(tabState.cost)}
            </button>
          )}
          {tabState.kind === 'later' && (
            <span className="dl-composer__row-empty">
              {t.army.tierRequires} {formatTier(tabState.requiresTier)}
            </span>
          )}
        </div>

        <div className="dl-composer__cards">
          {getCreepsForTab(tab).map((creep) => {
            const gesperrt = creep.tier > snapshot.tier;
            return (
              <CreepCard
                key={creep.id}
                creep={creep}
                blocker={
                  gesperrt
                    ? 'tier_locked'
                    : canAddCreep(creep, draft, snapshot, snapshot.enemyLaneCreeps).reason
                }
                requiresTier={gesperrt ? creep.tier : null}
                onAdd={() => onAdd(creep.id)}
              />
            );
          })}
        </div>

        {/*
          Die drei Reihen zeigen, in welcher Reihenfolge die Welle beim Gegner
          ankommt: erst FRONT, dann CENTER, dann BACK (siehe wave-rows.ts).
        */}
        <div className="dl-composer__rows">
          {WAVE_ROWS.map((row) => (
            <div key={row} className={`dl-composer__row dl-composer__row--${row}`}>
              <span className="dl-composer__row-label">
                {t.army.rows[row]}
                <span className="dl-composer__row-note">{t.army.rowNote[row]}</span>
              </span>

              <div className="dl-composer__row-creeps">
                {draft[row].length === 0 && (
                  <span className="dl-composer__row-empty">{t.army.emptyRow}</span>
                )}
                {groupRow(draft[row]).map(({ creepId, count }) => {
                  const creep = getCreep(creepId);
                  if (!creep) return null;
                  return (
                    <WaveEntry
                      key={creepId}
                      creep={creep}
                      count={count}
                      canAdd={
                        canAddCreep(creep, draft, snapshot, snapshot.enemyLaneCreeps).reason ===
                        null
                      }
                      onAdd={() => onAdd(creepId)}
                      onRemove={() => onRemove(creepId)}
                    />
                  );
                })}
              </div>

              {/* Wie viele hier stehen. Eine Obergrenze je Reihe gibt es nicht -
                  die Grenze sind die Army Points (Produktentscheidung 27.09.2026). */}
              <span className="dl-composer__row-count" title={t.army.rowCount}>
                {draft[row].length}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Die Zusammenfassung und der wichtigste Knopf des Fensters. */}
      <div className="dl-composer__side">
        <span className="dl-summary__title">{t.army.summaryTitle}</span>

        <dl className="dl-summary">
          <div className="dl-summary__row">
            <dt>{t.army.units}</dt>
            <dd>{einheiten}</dd>
          </div>
          <div className={'dl-summary__row' + (summary.affordable ? '' : ' dl-summary__row--warn')}>
            <dt>{t.army.cost}</dt>
            <dd className="dl-summary__gold">{formatGold(summary.cost)}</dd>
          </div>
          <div className="dl-summary__row">
            <dt>{t.army.incomePreview}</dt>
            <dd className="dl-summary__income">
              {summary.incomeGain > 0 ? `+${formatGold(summary.incomeGain)}` : '±0'}
            </dd>
          </div>
          <div className={'dl-summary__row' + (ueberCap ? ' dl-summary__row--warn' : '')}>
            <dt>{t.army.armyPoints}</dt>
            <dd>
              {summary.armyPoints} / {summary.armyPointCap}
            </dd>
          </div>
          <div className={'dl-summary__row' + (laneVoll ? ' dl-summary__row--warn' : '')}>
            <dt>{t.army.enemyLaneShort}</dt>
            <dd>
              {snapshot.enemyLaneCreeps + einheiten} / {MAX_SENT_CREEPS_PER_LANE}
            </dd>
          </div>
        </dl>

        {/*
          Alles, was zur Welle gehoert, sitzt am unteren Ende der Spalte
          (Rueckmeldung vom 28.09.2026). Vorher stand es als eigene Zeile unter
          den drei Reihen - dort kostete es genau die Hoehe, die den Reihen
          fehlt, um die Creep-Symbole gross genug zu zeigen.

          Reihenfolge von oben nach unten: erst womit man baut (Speicher,
          letzte Welle), dann was zurueckstellt (CLEAR), ganz unten der Knopf.
        */}
        <div className="dl-composer__side-foot">
          {/* Drei Speicherplaetze - kleiner als SEND, wie die Spezifikation
              es verlangt (Abschnitt 44). */}
          <span className="dl-slots-wave">
            <span className="dl-slots-wave__title">{t.army.saveSlots}</span>
            <span className="dl-slots-wave__row">
              {slots.map((welle, index) => (
                <WaveSlotButton
                  key={index}
                  nummer={index + 1}
                  welle={welle}
                  snapshot={snapshot}
                  onLoad={() => welle && onLoad(welle)}
                  onSave={() => speichereAuf(index)}
                />
              ))}
            </span>
          </span>

          <div className="dl-history">
            <span className="dl-history__title">{t.army.history}</span>
            {letzteWelle ? (
              <button
                type="button"
                className={'dl-slot-wave dl-slot-wave--last' + letzterZustand.klassen}
                title={`${t.army.history} - ${t.army.load}`}
                aria-label={
                  `${t.army.history}: ${formatGold(letzterZustand.kosten)} ${t.economy.gold}` +
                  letzterZustand.grund
                }
                {...letzteLaden}
              >
                <span className="dl-slot-wave__number">{t.army.load}</span>
                <span className="dl-slot-wave__cost">{formatGold(letzterZustand.kosten)}</span>
              </button>
            ) : (
              <span className="dl-composer__row-empty">{t.army.historyEmpty}</span>
            )}
          </div>

          <button
            type="button"
            className="dl-composer__clear"
            disabled={isDraftEmpty(draft)}
            {...leeren}
          >
            <span aria-hidden="true">🗑</span> {t.army.clear}
          </button>

          {/*
            Nur der Knopf, ohne Preis (Rueckmeldung vom 27.09.2026): Die Kosten
            stehen direkt darueber in der Zusammenfassung. Zweimal dieselbe Zahl
            macht den wichtigsten Knopf des Fensters nur unruhig.
          */}
          <button
            type="button"
            className="dl-composer__send"
            disabled={blocker !== null}
            {...senden}
          >
            <span className="dl-composer__send-label">
              <span aria-hidden="true">➤</span> {t.army.send}
            </span>
          </button>

          {blocker === 'send_cooldown' ? (
            <span className="dl-composer__send-reason">
              {t.army.blocked.send_cooldown} {Math.ceil(snapshot.waveCooldown)}s
            </span>
          ) : (
            blocker && <span className="dl-composer__send-reason">{t.army.blocked[blocker]}</span>
          )}
        </div>
      </div>
    </div>
  );
}
